/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import React from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	Popover,
	TextField,
} from '@material-ui/core';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import InfoRounded from '@material-ui/icons/InfoRounded';
import CancelIcon from '@material-ui/icons/Cancel';
import { CreateCourseViewModel } from '../CreateCourseViewModel';
import { useViewModel } from '../../../../../hooks/useViewModel';
import CreateCourseDialogViewStyles from './CreateCourseDialogViewStyles';
import { FormattedMessage, useIntl } from 'react-intl';
import { observer } from 'mobx-react';
import 'emoji-mart/css/emoji-mart.css';
import { Emoji, Picker } from 'emoji-mart';

const CreateCourseView = observer((): React.ReactElement => {
	const classes = CreateCourseDialogViewStyles();
	const viewModel: CreateCourseViewModel = useViewModel(CreateCourseViewModel);
	const translation = useIntl();

	return (
		<>
			<Button variant='contained' color='secondary' disableElevation onClick={() => viewModel.dialog.openDialog()}>
				<AddCircleIcon fontSize='small' />
			</Button>
			<Dialog
				open={viewModel.dialog.open}
				onClose={() => {
					viewModel.closeCreateDialog();
				}}
				aria-labelledby='create-course-title'
				aria-describedby='create-course-description'
				classes={{ paper: classes.paper }}>
				<DialogTitle disableTypography className={classes.dialogTitle} id='create-course-title'>
					<FormattedMessage id='courses.create_course_dialog.title' />
					<CancelIcon
						className={classes.closeButton}
						fontSize={'small'}
						onClick={() => {
							viewModel.closeCreateDialog();
						}}
					/>
				</DialogTitle>
				<DialogContent>
					<Grid container spacing={2}>
						<Grid item xs={3}>
							<label id='identifier' className={classes.label}>
								<FormattedMessage id={'courses.create_course_dialog.identifier'} />
							</label>
							<Box className={classes.emojiPicker} onClick={viewModel.popover.openPopover}>
								<Emoji emoji={viewModel.getEmoji()} size={22} />
							</Box>
							<Popover
								id={viewModel.popover.open ? 'pickEmoji popover' : undefined}
								open={viewModel.popover.open}
								anchorEl={viewModel.popover.anchor}
								onClose={viewModel.popover.closePopover}
								anchorOrigin={{
									vertical: 'bottom',
									horizontal: 'left',
								}}
								transformOrigin={{
									vertical: 'top',
									horizontal: 'left',
								}}>
								{viewModel.popover.open && (
									<Picker
										set='apple'
										onSelect={(emoji) => {
											viewModel.setEmoji(emoji);
											viewModel.popover.closePopover();
										}}
										title={translation.formatMessage({ id: 'courses.create_course_dialog.choose_emoji' })}
										emoji='point_up'
										i18n={{
											search: `${translation.formatMessage({ id: 'courses.create_course_dialog.search_emoji' })}`,
											categories: {
												search: `${translation.formatMessage({
													id: 'courses.create_course_dialog.search_emoji_results',
												})}`,
												recent: `${translation.formatMessage({
													id: 'courses.create_course_dialog.recent_searched_emoji',
												})}`,
											},
										}}
									/>
								)}
							</Popover>
						</Grid>
						<Grid item xs={9}>
							<label id='courseName' className={classes.label}>
								<FormattedMessage id={'courses.create_course_dialog.course_name'} />
							</label>
							<TextField
								required
								name='courseName'
								variant='outlined'
								fullWidth={true}
								size={'small'}
								value={viewModel.getCourseName()}
								onChange={(event) => viewModel.setCourseName(event)}
							/>
						</Grid>

						<Grid item xs={12}>
							<label id='courseStartDate' className={classes.label}>
								<FormattedMessage id={'courses.create_course_dialog.course_start_date'} />
							</label>
							<MuiPickersUtilsProvider utils={DateFnsUtils}>
								<KeyboardDatePicker
									className={classes.startDatePicker}
									size={'small'}
									disableToolbar
									variant='inline'
									inputVariant='outlined'
									format='dd.MM.yyyy'
									margin='normal'
									id='date-picker-inline'
									value={viewModel.getStartDate()}
									onChange={(newDate) => viewModel.setStartDate(newDate)}
									KeyboardButtonProps={{
										'aria-label': 'change date',
									}}
									autoOk
								/>
							</MuiPickersUtilsProvider>
						</Grid>
					</Grid>
				</DialogContent>
				<Grid item xs={12} className={classes.infoBox}>
					<Grid container direction='row'>
						<Grid item xs={2} className={classes.infoIconBox}>
							<InfoRounded />
						</Grid>
						<Grid item xs={10}>
							<FormattedMessage id={'courses.create_course_dialog.course_successfully_created_hint'} />
						</Grid>
					</Grid>
				</Grid>
				<DialogActions className={classes.dialogActions}>
					<Button
						disabled={viewModel.disableCreateButton()}
						onClick={() =>
							viewModel.createCourse(viewModel.getEmoji(), viewModel.getCourseName(), viewModel.getStartDate())
						}
						variant='contained'
						color='secondary'>
						<FormattedMessage id={'courses.create_course_dialog.create'} />
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
});

export default CreateCourseView;
